import type { QueryClient } from '@tanstack/react-query';
import type {
  ColumnDef,
  FilterFn,
  Row,
  SortingFn,
  SortingState,
  Table as ReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { rankings, rankItem } from '@tanstack/match-sorter-utils';
import { createColumnHelper } from '@tanstack/react-table';
import { download, generateCsv, mkConfig } from 'export-to-csv';
import { type MouseEvent, useCallback, useMemo, useRef } from 'react';

import type { DateTime } from '../../topics/time';
import { safeParseAddress4, ZERO_CIDR } from '../../topics/ipv4';
import { parseDateTime } from '../../topics/time';

export type { ColumnDef, Row, SortingState };
export { createColumnHelper, rankings, rankItem };

export interface AutoCollectionHandle<D = any> {
  exportToCSV: () => void;
  filteredRows: Row<D>[];
  data: D[];
}

export interface TablePresetsState {
  // Data
  presets: readonly { id: string; name: string }[];
  defaultPresetId: string | undefined;
  selectedPresetId: string | undefined;
  isPresetDirty: boolean;

  // Preset management
  renamePreset: (id: string, newName: string) => void;
  deletePreset: (id: string) => boolean;
  duplicatePreset: (id: string, newName: string) => void;
  setDefaultPreset: (id: string) => void;
  clearDefaultPreset: () => void;

  // Table state integration
  applyPreset: (presetId: string) => void;
  saveCurrentAsPreset: (name: string, description?: string) => void;
  updateSelectedPreset: () => void;
  clearPreset: () => void;
}

export interface AutoCollectionStateInterface<D, K extends string = string> {
  // Column management state
  columnOrder: K[];
  setColumnOrder: (order: K[]) => void;
  defaultColumnOrder: K[];
  resetColumnOrder: () => void;
  handleColumnReorder: (fromIndex: number, toIndex: number) => void;

  columnVisibility: IDColumnVisibility<K>;
  setColumnVisibility: (visibility: IDColumnVisibility<K>) => void;
  defaultColumnVisibility: IDColumnVisibility<K>;
  setDefaultColumnVisibility: (visibility: IDColumnVisibility<K>) => void;
  resetColumnVisibility: () => void;

  // Row management state
  enableRowReordering: boolean;
  rowOrder: D[];
  setRowOrder: (newOrder: D[]) => void;
  isRowOrderDirty: boolean;
  setDefaultRowOrder: (newOrder: D[]) => void;
  resetRowOrder: () => void;
  handleRowReorder: (fromIndex: number, toIndex: number) => void;
  isServerSidePaginated: boolean;

  // Pagination state
  offset: number;
  setOffset: (offset: number) => void;
  limit: number;
  setLimit: (limit: number) => void;
  numRows: number;
  setNumRows: (numRows: number) => void;
  resetPagination: () => void;
  resetToFirstPage: () => void;

  // Sorting state
  sortingState: IDColumnSort<K>[];
  setSortingState: (sorting: IDColumnSort<K>[]) => void;
  defaultSortingState: IDColumnSort<K>[];
  resetSorting: () => void;
  resetToDefaultSorting: () => void;

  // Filter state
  columnFilters: IDColumnFilter<K>[];
  setColumnFilters: (filters: IDColumnFilter<K>[]) => void;
  defaultColumnFilters: IDColumnFilter<K>[];
  resetFilters: () => void;

  // Debounced filter state for smoother UI
  debouncedColumnFilters: IDColumnFilter<K>[];
  debouncedGlobalFilter: string;
  isFilterPending: boolean;

  // Global filter and time period state
  globalFilter: string;
  setGlobalFilter: (filter: string) => void;
  defaultGlobalFilter: string;
  resetGlobalFilter: () => void;

  // Editing state
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;

  // Utility functions
  storageKey?: string;
  resetAll: () => void;
  isColumnOrderDefault: boolean;
  isColumnVisibilityDefault: boolean;
  isSortingDefault: boolean;
  isFiltersDefault: boolean;
  isPaginationDefault: boolean;
  isGlobalFilterDefault: boolean;
  isCurrentTimePeriodDefault: boolean;

  // Presets functionality (provided by consumers that support presets)
  presets?: TablePresetsState;

  tableRef: React.RefObject<AutoCollectionHandle<D> | null>;
}

export type FilterBaseProps = {
  key: string;
};

export type BoolFilter = FilterBaseProps & {
  value: boolean;
};

export type MultiSelectFilter = FilterBaseProps & {
  values: string[];
};

export type NumberFilter = FilterBaseProps & {
  value: number;
};

export type DateRangeFilter = FilterBaseProps & {
  start: DateTime | null;
  end: DateTime | null;
};

export type NumberRangeFilter = FilterBaseProps & {
  start: number | null;
  end: number | null;
};

export type SelectFilter = FilterBaseProps & {
  value: string;
};

export type TextFilter = FilterBaseProps & {
  value: string;
};

export type Filter =
  | (BoolFilter & FilterBaseProps & { type: TableFilterVariant.Bool })
  | (DateRangeFilter & FilterBaseProps & { type: TableFilterVariant.DateRange })
  | (MultiSelectFilter & FilterBaseProps & { type: TableFilterVariant.MultiSelect })
  | (NumberFilter & FilterBaseProps & { type: TableFilterVariant.Number })
  | (NumberRangeFilter & FilterBaseProps & { type: TableFilterVariant.NumberRange })
  | (SelectFilter & FilterBaseProps & { type: TableFilterVariant.Select })
  | (TextFilter & FilterBaseProps & { type: TableFilterVariant.Text });

export enum TableFilterVariant {
  Bool = 'bool',
  DateRange = 'date-range',
  MultiSelect = 'multi-select',
  Number = 'number',
  NumberRange = 'number-range',
  Select = 'select',
  Text = 'text',
}

export interface TableFilterOption {
  disabled: boolean;
  label: string;
  value: string;
}

export interface TableFilterDefinition {
  id: string;
  label: string;
  options: TableFilterOption[];
  variant: TableFilterVariant;
}

export interface Page<T> {
  items: T[];
  page: number;
  totalPages: number;
  totalCount: number;
}

export interface Table<R> {
  error: Error | null;
  isLoading: boolean;
  filterDefinitions: TableFilterDefinition[] | undefined;
  page: Page<R>;
  limit: number;
  filters: Filter[];

  getFilterDefinition: (filterID: string) => TableFilterDefinition | undefined;
  setFilters: (filteration: Filter[]) => void;
  getFilterByID: (filterID: string) => Filter | undefined;
  clearFilter: (filterID: string) => void;
  updateFilter: (filter: Filter) => void;
  setLimit: (limit: number) => void;
  goTo(page: number): void;
  invalidatePage(client: QueryClient): void;
}

export interface IDColumnFilter<K extends string, V = unknown> {
  id: K;
  value: V;
}
export interface IDColumnSort<K extends string> {
  id: K;
  desc: boolean;
}

export type IDColumnVisibility<K extends string> = Partial<Record<K, boolean>>;

const equalsIfSet: FilterFn<any> = (row, columnId: string, filterValue: unknown) =>
  filterValue == null || row.getValue(columnId) === filterValue;

equalsIfSet.autoRemove = (val: any) => val == null;

const arrIncludesIfSet: FilterFn<any> = (row, columnId: string, filterValue: unknown[]) =>
  !filterValue?.length || filterValue.includes(row.getValue(columnId));

arrIncludesIfSet.autoRemove = (val: any) => !val;

const prefixOverlapsIfSet: FilterFn<any> = (row, columnId: string, filterValue: string) => {
  const prefix = row.getValue<string | undefined>(columnId);

  if (!prefix || prefix === ZERO_CIDR) return true;

  const filterIP = safeParseAddress4(filterValue);
  if (!filterIP) return true;

  const prefixAddr = safeParseAddress4(prefix);
  if (!prefixAddr) return true;

  return filterIP.isInSubnet(prefixAddr) || prefixAddr.isInSubnet(filterIP);
};

prefixOverlapsIfSet.autoRemove = (val: any) => !val;

const inNumberRange: FilterFn<any> = (
  row,
  columnId: string,
  filterValue: [number | null, number | null],
) => {
  const [min, max] = filterValue;
  const value = row.getValue<number>(columnId);

  // if min > max, filter out all rows (as this makes for an invalid range)
  if (min != null && max != null && min > max) {
    return false;
  }

  // standard range logic otherwise
  if (min != null && max != null) {
    return value >= min && value <= max;
  }
  if (min != null) {
    return value >= min;
  }
  if (max != null) {
    return value <= max;
  }
  return true;
};

inNumberRange.autoRemove = (val: any) =>
  !val || (Array.isArray(val) && val[0] == null && val[1] == null);

/** Combined main value + subline from column meta for filtering. */
export function getValueWithSubline(row: Row<any>, columnId: string): string {
  const mainValue = row.getValue(columnId);
  let sublineValue: any = null;
  try {
    const table = (row as any).table || (row as any)._table;
    if (table && typeof table.getColumn === 'function') {
      const columnDef = table.getColumn(columnId)?.columnDef;
      if (columnDef?.meta?.subline && typeof columnDef.meta.subline === 'function') {
        sublineValue = columnDef.meta.subline(row.original);
      }
    }
  } catch {
    // ignore
  }
  return [
    mainValue != null ? String(mainValue) : '',
    sublineValue != null ? String(sublineValue) : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/** Text filter that matches against both the main value and subline (from meta.subline) when set. */
const includesSublineIfSet: FilterFn<any> = (row, columnId: string, filterValue: unknown) => {
  if (filterValue == null || String(filterValue).trim() === '') return true;
  const search = String(filterValue).toLowerCase();
  const combined = getValueWithSubline(row, columnId).toLowerCase();
  return combined.includes(search);
};

includesSublineIfSet.autoRemove = (val: any) => !val || String(val).trim() === '';

/** Filter function that filters only on the subline value (from meta.subline).
 * This is used with virtual subline columns that have an accessorFn extracting the subline value.
 * The column's accessorFn already provides the subline value, so we just filter on it directly.
 */
const sublineOnly: FilterFn<any> = (row, columnId: string, filterValue: unknown) => {
  if (filterValue == null || String(filterValue).trim() === '') return true;
  // The virtual column's accessorFn already extracts the subline value
  const sublineValue = row.getValue(columnId);
  if (sublineValue == null) return true; // If no subline, don't filter out
  const search = String(filterValue).toLowerCase();
  return String(sublineValue).toLowerCase().includes(search);
};

sublineOnly.autoRemove = (val: any) => !val || String(val).trim() === '';

const inDateTimeRange: FilterFn<any> = (
  row,

  columnId: string,

  filterValue: [string | null, string | null],
) => {
  const [minTimestamp, maxTimestamp] = filterValue;
  const min = parseDateTime(minTimestamp);
  const max = parseDateTime(maxTimestamp);

  const value = row.getValue<string>(columnId);
  const dt = parseDateTime(value);
  if (!dt) return true;

  // if min > max, filter out all rows (as this makes for an invalid range)
  if (min != null && max != null && min > max) {
    return false;
  }

  // standard range logic otherwise
  if (min != null && max != null) {
    return dt >= min && dt <= max;
  }

  if (min != null) {
    return dt >= min;
  }

  if (max != null) {
    return dt <= max;
  }

  return true;
};

inDateTimeRange.autoRemove = (val: any) =>
  !val || (Array.isArray(val) && val[0] == null && val[1] == null);

function punctuationAgnosticTextFilterMatches(
  cellValue: string | null | undefined,
  filterValue: unknown,
  normalize: (value: string) => string,
): boolean {
  if (filterValue == null || String(filterValue).trim() === '') return true;
  const needle = normalize(String(filterValue));
  if (!needle) return true;
  const haystack = normalize(String(cellValue ?? ''));
  return haystack.includes(needle);
}

const normalizeMacAddressFilterText = (value: string) =>
  value.replace(/[^0-9a-fA-F]/g, '').toLowerCase();

const normalizeAlphanumericFilterText = (value: string) =>
  value.replace(/[^0-9a-zA-Z]/g, '').toLowerCase();

function isMacAddressLikeSearch(filterValue: string): boolean {
  const letters = filterValue.match(/[a-zA-Z]/g);
  if (!letters?.length) return true;
  return letters.every((letter) => /[a-fA-F]/.test(letter));
}

// Punctuation-agnostic MAC address text filter
export function macAddressTextFilterMatches(
  cellValue: string | null | undefined,
  filterValue: unknown,
): boolean {
  if (filterValue == null || String(filterValue).trim() === '') return true;
  if (!isMacAddressLikeSearch(String(filterValue))) return false;
  return punctuationAgnosticTextFilterMatches(
    cellValue,
    filterValue,
    normalizeMacAddressFilterText,
  );
}

// Punctuation-agnostic alphanumeric text filter (e.g. client names)
export function alphanumericTextFilterMatches(
  cellValue: string | null | undefined,
  filterValue: unknown,
): boolean {
  return punctuationAgnosticTextFilterMatches(
    cellValue,
    filterValue,
    normalizeAlphanumericFilterText,
  );
}

const macAddressTextIncludesIfSet: FilterFn<any> = (row, columnId: string, filterValue: unknown) =>
  macAddressTextFilterMatches(row.getValue(columnId) as string | null | undefined, filterValue);

macAddressTextIncludesIfSet.autoRemove = (val: any) => !val || String(val).trim() === '';

const alphanumericTextIncludesIfSet: FilterFn<any> = (
  row,
  columnId: string,
  filterValue: unknown,
) =>
  alphanumericTextFilterMatches(row.getValue(columnId) as string | null | undefined, filterValue);

alphanumericTextIncludesIfSet.autoRemove = (val: any) => !val || String(val).trim() === '';

export const filterFns = {
  equalsIfSet,
  arrIncludesIfSet,
  prefixOverlapsIfSet,
  inNumberRange,
  inDateTimeRange,
  includesSublineIfSet,
  sublineOnly,
  macAddressTextIncludesIfSet,
  alphanumericTextIncludesIfSet,
} as const;

declare module '@tanstack/table-core' {
  interface FilterFns {
    equalsIfSet: FilterFn<any>;
    arrIncludesIfSet: FilterFn<any>;
    prefixOverlapsIfSet: FilterFn<any>;
    inNumberRange: FilterFn<any>;
    inDateTimeRange: FilterFn<any>;
    includesSublineIfSet: FilterFn<any>;
    sublineOnly: FilterFn<any>;
    macAddressTextIncludesIfSet: FilterFn<any>;
    alphanumericTextIncludesIfSet: FilterFn<any>;
  }
}

const sortingCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

const collator: SortingFn<any> = (rowA, rowB, columnID): number =>
  sortingCollator.compare(rowA.getValue(columnID), rowB.getValue(columnID));

export const sortingFns = {
  collator,
} as const;

// Shared utility functions to reduce duplication between AutoTable and AutoBlocks

const macRegexp = /([\da-fA-F]{2}[-.:_]){5}[\da-fA-F]{2}/gi;

export function normalizeMACs(input: string): string {
  const matches = input.matchAll(macRegexp);
  if (matches) {
    let s = input;
    for (const match of matches) {
      s = s.replace(match[0], match[0].replaceAll(/[-.:_]/g, ':'));
    }
    return s;
  }

  return input;
}

// Based on https://tanstack.com/table/v8/docs/api/features/filters#filter-meta
export function defaultGlobalFilterFn<D extends any>(
  row: Row<D>,
  columnId: string,
  filterValue: string,
  addMeta: (meta: any) => void,
) {
  const pattern = normalizeMACs(filterValue);
  const valueToFilter = getValueWithSubline(row, columnId) || row.getValue(columnId);
  const itemRank = rankItem(valueToFilter, pattern);

  addMeta({ itemRank });

  return itemRank.passed;
}

export function getRowRange<T extends any>(
  rows: Row<T>[],
  startIndex: number,
  endIndex: number,
): Row<T>[] {
  const rangeStart = Math.min(startIndex, endIndex);
  const rangeEnd = Math.max(startIndex, endIndex);
  if (rangeStart < 0 || rangeEnd < 0) return [];
  return rows.slice(rangeStart, rangeEnd + 1);
}

export interface RowSelectionTracker {
  getLastSelectedId: () => number | undefined;
  setLastSelectedId: (id?: number) => void;
}

export function useSelectionTracker(): RowSelectionTracker {
  const lastSelectedIdRef = useRef<number | undefined>(undefined);

  return useMemo<RowSelectionTracker>(
    () => ({
      getLastSelectedId: () => lastSelectedIdRef.current,
      setLastSelectedId: (id) => {
        lastSelectedIdRef.current = id;
      },
    }),
    [],
  );
}

export function toggleRowSelection<D>(
  row: Row<D>,
  table: ReactTable<D>,
  selectionTracker: RowSelectionTracker,
  shiftKey: boolean,
) {
  const lastSelectedId = selectionTracker.getLastSelectedId();
  if (shiftKey && lastSelectedId !== undefined) {
    const { rows } = table.getSortedRowModel();
    const startIndex = rows.findIndex((r) => r.index === row.index);
    const endIndex = rows.findIndex((r) => r.index === lastSelectedId);
    const rowsToToggle = getRowRange(rows, startIndex, endIndex);
    const isCellSelected = row.getIsSelected();

    table.setRowSelection((old) => ({
      ...old,
      ...Object.fromEntries(rowsToToggle.map((r) => [r.id, !isCellSelected])),
    }));
  } else {
    row.toggleSelected();
  }
  selectionTracker.setLastSelectedId(row.index);
}

export function useRowCheckboxHandlers<D>(
  row: Row<D>,
  table: ReactTable<D>,
  selectionTracker: RowSelectionTracker | undefined,
) {
  const shiftClickRef = useRef(false);

  const handleChange = useCallback(
    (isSelected: boolean) => {
      if (shiftClickRef.current) {
        shiftClickRef.current = false;
        return;
      }
      if (row.getIsSelected() === isSelected) return;
      row.toggleSelected(isSelected);
      selectionTracker?.setLastSelectedId(row.index);
    },
    [row, selectionTracker],
  );

  const handleClick = useCallback(
    (e: MouseEvent<HTMLInputElement>) => {
      e.stopPropagation();
      if (e.shiftKey && selectionTracker) {
        e.preventDefault();
        shiftClickRef.current = true;
        toggleRowSelection(row, table, selectionTracker, true);
      }
    },
    [row, table, selectionTracker],
  );

  return { handleChange, handleClick };
}

/**
 * Expands columns to include virtual subline filter columns when a column has sublineFilterVariant.
 * These virtual columns are hidden but allow filtering on subline values independently.
 */
export function expandColumnsWithSublineFilters<D extends any>(
  columns: ColumnDef<D, any>[],
): ColumnDef<D, any>[] {
  const expanded: ColumnDef<D, any>[] = [];

  for (const col of columns) {
    expanded.push(col);

    // If column has subline filter configuration, create a virtual column for it
    if (
      col.id &&
      (col as any).meta?.subline &&
      (col as any).sublineFilterVariant &&
      ((col as any).sublineFilterFn || (col as any).sublineFilter)
    ) {
      const sublineId = `${col.id}-subline`;
      const sublineFn = (col as any).meta.subline as (row: D) => any;

      expanded.push({
        id: sublineId,
        accessorFn: (row: D) => {
          const value = sublineFn(row);
          return value != null ? String(value) : null;
        },
        filterFn: (col as any).sublineFilterFn || 'sublineOnly',
        enableSorting: false,
        enableHiding: false,
        enableColumnFilter: true,
        // Hide this column from display - it's only for filtering
        meta: {
          ...((col as any).meta || {}),
        },
      } as ColumnDef<D, any>);
    }
  }

  return expanded;
}

export function createDefaultColumnVisibilityEffect<D extends any, C extends ColumnDef<D, any>>(
  columns: C[],
  filteredRows: Row<D>[],
  setColumnVisibility: React.Dispatch<React.SetStateAction<VisibilityState>>,
  columnVisibility?: VisibilityState,
) {
  if (filteredRows.length) {
    // Start with existing visibility state or default all columns to visible
    const visibility = { ...columnVisibility };

    // Set default visibility for columns that don't have a visibility state
    // Skip subline filter columns (they should always be hidden)
    columns.forEach((col) => {
      if (col.id && visibility[col.id] === undefined) {
        // Subline filter columns (ending in -subline) should always be hidden
        if (col.id.endsWith('-subline')) {
          visibility[col.id] = false;
        } else {
          visibility[col.id] = true; // Default to visible
        }
      }
    });

    // Handle hideIfAllNullish columns
    const hideIfAllNullishColumns = columns.filter((col) => col.meta?.hideIfAllNullish);
    if (hideIfAllNullishColumns.length > 0) {
      hideIfAllNullishColumns
        .filter((col): col is typeof col & { id: NonNullable<(typeof col)['id']> } => !!col.id)
        .forEach((col) => {
          const hasNonNullishValue = filteredRows.some((row) => {
            const value = row.getValue(col.id);
            return value != null;
          });

          visibility[col.id] = hasNonNullishValue;
        });
    }

    // Handle hideIfAllEqual columns
    const hideIfAllEqualColumns = columns.filter((col) => col.meta?.hideIfAllEqual);
    if (hideIfAllEqualColumns.length > 0) {
      hideIfAllEqualColumns
        .filter((col): col is typeof col & { id: NonNullable<(typeof col)['id']> } => !!col.id)
        .forEach((col) => {
          const firstVal = filteredRows[0]?.getValue(col.id);
          visibility[col.id] = filteredRows.some((row) => row.getValue(col.id) !== firstVal);
        });
    }

    // Only update if visibility has actually changed to prevent infinite loops
    const hasChanged =
      Object.keys(visibility).some((key) => visibility[key] !== columnVisibility?.[key]) ||
      Object.keys(columnVisibility ?? {}).some(
        (key) => columnVisibility?.[key] !== visibility[key],
      );

    if (hasChanged) {
      setColumnVisibility(visibility);
    }
  }
}

export function createCSVExportHandler(table: ReactTable<any>, exportToCSVFilename?: string) {
  return () => {
    const { rows } = table.getRowModel();

    const filename = exportToCSVFilename?.replace(/\.csv$/, '') ?? document.title ?? 'export';
    const csvConfig = mkConfig({
      fieldSeparator: ',',
      quoteStrings: true,
      decimalSeparator: '.',
      showTitle: false,
      filename,
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true,
    });

    const csvData = rows.map((row) =>
      Object.fromEntries(
        row
          .getVisibleCells()
          // display columns don't have an accessor function
          .filter((cell) => !!cell.column.accessorFn)
          .map((cell) => [
            cell.column.columnDef.header &&
            ['string', 'number'].includes(typeof cell.column.columnDef.header)
              ? cell.column.columnDef.header
              : cell.column.columnDef?.meta?.tooltip?.label?.contents ?? '',
            cell.getValue() ?? '',
          ]),
      ),
    );

    const csv = generateCsv(csvConfig)(csvData);
    download(csvConfig)(csv);
  };
}

// react-table throws errors when getting row model if the sort/filter
// state is invalid. This is probably just because of the global URL-based
// search state becoming outdated, so we try resetting the filter and sort
// state and getting the rows again once.
export function getRows<D>(table: ReactTable<D>) {
  try {
    return table.getRowModel()?.rows ?? [];
  } catch (err) {
    table.resetSorting();
    table.resetGlobalFilter();
    table.resetColumnFilters();
    return table.getRowModel()?.rows ?? [];
  }
}

export function getPageRows<T>(
  rows: T[],
  offset: number | null | undefined,
  limit: number | null | undefined,
  isServerSidePaginated = false,
): T[] {
  if (offset != null && limit != null && !isServerSidePaginated) {
    return rows.slice(offset, limit ? offset + limit : undefined);
  }
  return rows;
}

interface SelectableRowsInfo<D extends unknown = unknown> {
  selectableRows: Row<D>[];
  allSelectableRowsSelected: boolean;
  someSelectableRowsSelected: boolean;
}

function getSelectableRowsInfo<D extends unknown = unknown>(
  tableRows: Row<D>[],
  enableMultiRowSelection: boolean | ((row: Row<D>) => boolean) | any | null,
  multiSelectedRows?: Record<string, boolean>,
): SelectableRowsInfo<D> | null {
  if (!enableMultiRowSelection) return null;
  const selectableRows =
    typeof enableMultiRowSelection === 'function'
      ? tableRows.filter((row) => enableMultiRowSelection(row))
      : tableRows;

  // Use the actual multiSelectedRows state if available, otherwise fall back to row.getIsSelected()
  const allSelectableRowsSelected = multiSelectedRows
    ? selectableRows.every((row) => !!multiSelectedRows[row.id])
    : selectableRows.every((row) => row.getIsSelected());

  const someSelectableRowsSelected = multiSelectedRows
    ? selectableRows.some((row) => !!multiSelectedRows[row.id])
    : selectableRows.some((row) => row.getIsSelected());

  return { selectableRows, allSelectableRowsSelected, someSelectableRowsSelected };
}

export function countSelectedRows(selection?: Record<string, boolean>) {
  if (!selection) return 0;
  return Object.values(selection).filter(Boolean).length;
}

export function countSelectedRowsOnPage(
  selection: Record<string, boolean> | undefined,
  pageRows: { id: string }[],
) {
  if (!selection) return 0;
  return pageRows.reduce((count, row) => count + (selection[row.id] ? 1 : 0), 0);
}

export function createHeaderSelectionState<D extends unknown = unknown>(
  table: ReactTable<D>,
  rows: Row<D>[],
  enableMultiRowSelection: boolean | ((row: Row<D>) => boolean) | any | null,
  multiSelectedRows?: Record<string, boolean>,
) {
  const selectableRowsInfo = getSelectableRowsInfo(
    rows,
    enableMultiRowSelection,
    multiSelectedRows,
  );
  if (selectableRowsInfo) {
    return {
      ariaLabel: selectableRowsInfo.allSelectableRowsSelected
        ? 'Deselect all rows on this page'
        : 'Select all rows on this page',
      checked: selectableRowsInfo.allSelectableRowsSelected,
      indeterminate:
        !selectableRowsInfo.allSelectableRowsSelected &&
        selectableRowsInfo.someSelectableRowsSelected,
    };
  }

  return {
    ariaLabel: table.getIsAllPageRowsSelected()
      ? 'Deselect all rows on this page'
      : 'Select all rows on this page',
    checked: table.getIsAllPageRowsSelected(),
    indeterminate: !table.getIsAllPageRowsSelected() && table.getIsSomeRowsSelected(),
  };
}

function deselectRows<D>(selection: Record<string, boolean>, rows: Row<D>[]) {
  const nextSelection = { ...selection };
  for (const row of rows) {
    delete nextSelection[row.id];
  }
  return nextSelection;
}

function selectRows<D>(selection: Record<string, boolean>, rows: Row<D>[]) {
  return {
    ...selection,
    ...Object.fromEntries(rows.map((row) => [row.id, true])),
  };
}

export function selectAllToggleHandler<D extends unknown = unknown>(
  table: ReactTable<D>,
  enableMultiRowSelection: boolean | ((row: Row<D>) => boolean) | any | null,
  multiSelectedRows?: Record<string, boolean>,
  targetRows?: Row<D>[],
) {
  const rowsToToggle = targetRows ?? getRows(table);
  const selectableRowsInfo = getSelectableRowsInfo(
    rowsToToggle,
    enableMultiRowSelection,
    multiSelectedRows,
  );
  if (selectableRowsInfo) {
    if (selectableRowsInfo.allSelectableRowsSelected) {
      table.setRowSelection((old) => deselectRows(old, selectableRowsInfo.selectableRows));
    } else {
      table.setRowSelection((old) => selectRows(old, selectableRowsInfo.selectableRows));
    }
  } else {
    const selectableRows = rowsToToggle.filter((row) => row.getCanMultiSelect());
    const allSelectableRowsSelected = selectableRows.every((row) => row.getIsSelected());

    if (allSelectableRowsSelected) {
      table.setRowSelection((old) => deselectRows(old, selectableRows));
    } else {
      table.setRowSelection((old) => selectRows(old, selectableRows));
    }
  }
}

export function defaultColumnCanGlobalFilter(c: any): boolean {
  return c.columnDef.enableGlobalFilter ?? true;
}
